import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SEO from '../components/seo/SEO'

type GalleryImage = { id: string; src: string; alt: string; span?: 'tall' | 'wide' | 'large' }
type GallerySection = { id: string; title: string; images: GalleryImage[] }

const GAS_URL = process.env.REACT_APP_GAS_GALLERY_URL || ''
const ZM_IMAGES_JSON = '/zm/images.json'

function hasSectionsWithImages(data: { sections?: GallerySection[] }) {
  return data.sections?.length && data.sections.some((s) => s.images?.length)
}

export default function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [gallerySections, setGallerySections] = useState<GallerySection[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const allImages = gallerySections.flatMap((s) => s.images || [])
  const lightboxImg = lightboxIndex !== null ? allImages[lightboxIndex] : null
  const goPrev = () => setLightboxIndex((i) => (i === null ? null : (i - 1 + allImages.length) % allImages.length))
  const goNext = () => setLightboxIndex((i) => (i === null ? null : (i + 1) % allImages.length))

  useEffect(() => {
    if (lightboxIndex === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIndex(null)
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, allImages.length])

  useEffect(() => {
    // 1) Prefer local folder: public/zm — same origin, indexable
    fetch(ZM_IMAGES_JSON, { redirect: 'follow' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Not found'))))
      .then((data: { sections?: GallerySection[] }) => {
        if (hasSectionsWithImages(data)) {
          setGallerySections(data.sections!)
          setLoading(false)
          return
        }
        throw new Error('No images')
      })
      .catch(() => {
        // 2) Fallback: Google Apps Script (Drive)
        if (!GAS_URL) {
          setError('No gallery images. Add images to /zm and run: npm run scan-zm-images')
          setLoading(false)
          return
        }
        fetch(GAS_URL, { redirect: 'follow' })
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            return res.json()
          })
          .then((data: { sections?: GallerySection[]; error?: string }) => {
            if (data.error) setError('Drive error: ' + data.error)
            else if (data.sections?.length) setGallerySections(data.sections!)
            else setError('No images found.')
          })
          .catch((err) => {
            const script = document.createElement('script')
            const callbackName = '_gasGallery_' + Date.now()
            ;(window as unknown as Record<string, unknown>)[callbackName] = (data: { sections?: GallerySection[] }) => {
              delete (window as unknown as Record<string, unknown>)[callbackName]
              document.head.removeChild(script)
              if (data.sections?.length) setGallerySections(data.sections!)
              setLoading(false)
            }
            script.src = GAS_URL + '?callback=' + callbackName
            script.onerror = () => {
              setError('Failed to load images. Add images to public/zm and run: npm run scan-zm-images')
              setLoading(false)
            }
            document.head.appendChild(script)
          })
          .finally(() => setLoading(false))
      })
  }, [])

  return (
    <>
      <SEO
        title="Gallery | Logo Branded Door Mats Zambia - Berberpoint & Outdoor Mats"
        description="View our gallery of Berberpoint door mats and outdoor mats. Custom branded mats for homes and businesses across Zambia."
        keywords="door mat gallery Zambia, Berberpoint mats, outdoor mats, custom door mats gallery"
        url="https://www.logobrandeddoormats.co.zm/gallery"
      />
      <div className="min-h-screen w-full bg-white">
        <section className="w-full px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-8">
          {/* Title */}
          <div className="mb-6 sm:mb-8">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-1">Our collection</p>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">Photo Gallery</h1>
              {gallerySections.length > 0 && (
                <p className="text-sm sm:text-base text-gray-600 max-w-md">
                  {gallerySections.map((s) => s.title).join(' & ')}
                </p>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-24">
              <div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="py-16 text-center text-gray-500 text-sm">{error}</div>
          )}

          {/* Sections */}
          {!loading && !error && gallerySections.map((section, sectionIdx) => {
            const startIndex = gallerySections.slice(0, sectionIdx).reduce((n, s) => n + (s.images?.length || 0), 0)
            return (
            <div key={section.id} className="mb-10 sm:mb-12">
              <h2 className="text-lg font-semibold text-gray-800 mb-3 sm:mb-4">{section.title}</h2>
              {/* Masonry-style columns — no gaps */}
              <div className="columns-2 sm:columns-3 lg:columns-4 gap-2 sm:gap-3">
                {section.images.map((img: GalleryImage, i: number) => (
                  <motion.div
                    key={img.id}
                    className="break-inside-avoid mb-2 sm:mb-3 overflow-hidden rounded-lg cursor-pointer group"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: (i % 4) * 0.05 }}
                    onClick={() => setLightboxIndex(startIndex + i)}
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-auto block group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </motion.div>
                ))}
              </div>
            </div>
            )
          })}
        </section>
      </div>

      <AnimatePresence>
        {lightboxImg && (
          <>
            <motion.button
              type="button"
              aria-label="Close"
              className="fixed inset-0 bg-black/80 z-50 cursor-default"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setLightboxIndex(null)}
            />
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={() => setLightboxIndex(null)}
            >
              {/* Close button */}
              <button
                type="button"
                aria-label="Close"
                onClick={() => setLightboxIndex(null)}
                className="absolute top-4 right-4 z-[60] w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm flex items-center justify-center text-white text-2xl font-bold transition-colors"
              >
                ✕
              </button>
              {/* Prev */}
              <button
                type="button"
                aria-label="Previous"
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              {/* Next */}
              <button
                type="button"
                aria-label="Next"
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-[60] w-12 h-12 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
              <img
                src={encodeURI(lightboxImg.src)}
                alt={lightboxImg.alt}
                className="w-[100vw] h-[100vh] object-contain rounded-none"
                onClick={(e) => e.stopPropagation()}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
